// src/components/PurchaseFlow/Step/OfferStep.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import api from '../../../../services/api';
import { formatNumber, parseLocaleNumber } from '../../../../utils/numberFormatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBlender, faMobileAlt, faHome, faLaptop, faBox, faMotorcycle, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import 'react-loading-skeleton/dist/skeleton.css';
import './OfferStep.css';
import './DownpaymentPlans.css';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import { PurchaseFlowStateContext } from '../../../../context/PurchaseFlowStateContext';
import { ClientContext } from '../../../../context/ClientContext';
import { useAuth } from '../../../../context/AuthContext';
import AuthorizationModal from '../../../PurchaseFlow/AuthorizationModal';
import { handleValidationErrors } from '../../../../utils/errorHandler';
import { trackInstallmentPlanSelected, trackOffersSearched } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';
import OfferStepTitle from './Sections/OfferStepTitle';
import CategorySelector from './Sections/CategorySelector';
import ProductPlanSelector from './Sections/ProductPlanSelector';
import ProductForm from './Sections/ProductForm';
import OffersList from './Sections/OffersList';
import ChangeProductButton from './Sections/ChangeProductButton';
import BackButton from './Sections/BackButton';

const MySwal = withReactContent(Swal);

const OfferStep = ({ onNext, clientData, setPurchaseRequestId }) => {
  const { setSelectedOffer } = useContext(SelectedOfferContext);
  const { updateSaleMonitor } = useContext(PurchaseFlowStateContext);
  const { clientAuthorization } = useContext(ClientContext);
  const { commerceId, branchId: analyticsBranchId } = useAnalyticsContext();
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [maxAmountState, setMaxAmount] = useState(clientData?.available_credit || 0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProductPlan, setSelectedProductPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authorizationData, setAuthorizationData] = useState(null); // Estado para autorización manual
  const [categories, setCategories] = useState([]);
  const [authorizationsEnabled] = useState(process.env.REACT_APP_ENABLE_AUTHORIZATIONS === 'true');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [lastCalculatedAmount, setLastCalculatedAmount] = useState('');
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState(null);
  const { channelId, branchId } = useAuth();

  const [productNameConfig, setProductNameConfig] = useState('required');
  const [productDescriptionConfig, setProductDescriptionConfig] = useState('optional');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  const productLimits = clientData?.product_limits || null;
  const hasProductLimits = !!productLimits;
  const hasProductPlanSupport = categories.some(cat => 
    (cat.plans && Array.isArray(cat.plans) && cat.plans.length > 0) || 
    cat.has_plans === true
  );
  
  // Actualizar maxAmountState cuando clientData cambie
  useEffect(() => {
    if (clientData?.available_credit !== undefined) {
      setMaxAmount(clientData.available_credit);
    }
  }, [clientData?.available_credit]);

  const getSelectedCategoryData = () => categories.find(cat => cat.id === selectedCategory);

  const getEffectiveMaxAmount = (categoryData = getSelectedCategoryData()) => {
    const selectedPlanMaxAmount = Number(selectedProductPlan?.max_capital_effective ?? 0);
    if (selectedPlanMaxAmount > 0) {
      return selectedPlanMaxAmount;
    }

    const selectedProductMaxAmount = Number(categoryData?.max_capital_effective ?? 0);
    if (selectedProductMaxAmount > 0) {
      return selectedProductMaxAmount;
    }

    return Number(maxAmountState ?? 0);
  };

  // Auto-selección cuando hay un solo producto disponible
  useEffect(() => {
    const autoSelectSingleProduct = async () => {
      // Solo ejecutar si no hay categoría seleccionada y hay exactamente un producto
      if (!selectedCategory && !categoriesLoading && categories.length === 1) {
        const singleProduct = categories[0];
        
        // Auto-seleccionar el producto (plan_source='none' ya no deshabilita)
        setSelectedCategory(singleProduct.id);
        setSelectedProductPlan(null);
        
        // Si tiene planes, cargarlos dinámicamente
        if (singleProduct.has_plans !== false) {
          const loadedPlans = await fetchProductPlans(singleProduct.id);
          
          // Si hay solo un plan, auto-seleccionarlo
          if (loadedPlans && loadedPlans.length === 1) {
            setSelectedProductPlan(loadedPlans[0]);
          }
        }
      }
    };

    autoSelectSingleProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, categoriesLoading, selectedCategory]);
  
  useEffect(() => {
    const loadCategoriesFromAPI = async () => {
      try {
        const catalogUrl = clientData?.id
          ? `/customers/${clientData.id}/catalog/products`
          : '/catalog/products';
        const response = await api.get(catalogUrl);
        if (response.data.success && response.data.data.length > 0) {
          return response.data.data.map(category => {
            // Mapear iconos basado en el nombre del icono del backend
            const icon = {
              'faBlender': faBlender,
              'fa-solid fa-mobile': faMobileAlt,
              'fa-solid fa-home': faHome,
              'fa-solid fa-laptop': faLaptop,
              'fa-solid fa-motorcycle': faMotorcycle,
              'faDollarSign': faDollarSign,
              'fa-solid fa-dollar-sign': faDollarSign,
              'dinero': faDollarSign
            }[category.icon] || faDollarSign; // faDollarSign (dinero) como fallback

            return {
              id: category.id,
              name: category.name,
              code: category.code ?? category.alias,
              icon: icon,
              source: category.source ?? 'industry',
              plan_source: category.plan_source ?? 'none',
              has_plans: category.has_plans ?? (category.plan_source !== 'none'),
              max_capital_effective: category.max_capital_effective || 0,
              plans: [] // se cargarán dinámicamente
            };
          });
        }
        return null;
      } catch (error) {
        console.warn('Failed to load categories from API, falling back to environment variables:', error.message);
        return null;
      }
    };

    const loadCategoriesFromEnv = () => {
      const categoriesConfig = process.env.REACT_APP_PRODUCT_CATEGORIES;
      if (!categoriesConfig) return [];

      return categoriesConfig.split(',').map(category => {
        const [id, label, iconName] = category.split(':');
        const icon = {
          faBlender,
          faMobileAlt,
          faHome,
          faLaptop,
          faDollarSign,
          dinero: faDollarSign
        }[iconName] || faDollarSign;

        return { id, label, icon, source: 'industry', plan_source: 'none', has_plans: false };
      });
    };

    const loadCategories = async () => {
      setCategoriesLoading(true);
      
      // Intentar cargar desde API primero
      const apiCategories = await loadCategoriesFromAPI();
      
      if (apiCategories && apiCategories.length > 0) {
        console.log('Categories loaded from API');
        setCategories(apiCategories);
      } else {
        // Fallback a variables de entorno
        console.log('Categories loaded from environment variables');
        const envCategories = loadCategoriesFromEnv();
        setCategories(envCategories);
      }
      
      setCategoriesLoading(false);
    };

    // Cargar configuración de campos de producto
    const loadProductFieldConfig = () => {
      const nameConfig = process.env.REACT_APP_PRODUCT_NAME || 'required';
      const descriptionConfig = process.env.REACT_APP_PRODUCT_DESCRIPTION || 'optional';
      
      setProductNameConfig(nameConfig);
      setProductDescriptionConfig(descriptionConfig);
    };

    loadCategories();
    loadProductFieldConfig();
  }, [clientData?.id]);

  // Sincronizar estado del paso de oferta al monitor de venta (sidebar)
  useEffect(() => {
    if (!updateSaleMonitor) return;
    const cat = selectedCategory ? categories.find(c => c.id === selectedCategory) : null;
    updateSaleMonitor({
      selectedProduct: cat ? {
        id: cat.id,
        name: cat.name || cat.label,
        maxCapitalEffective: cat.max_capital_effective ?? 0
      } : null,
      selectedProductPlan: selectedProductPlan ? { id: selectedProductPlan.id, label: selectedProductPlan.label } : null,
      requestedAmount: amount ? parseFloat(amount) : null,
      selectedOfferSummary: selectedPlan ? { installments: selectedPlan.installments, installmentAmount: selectedPlan.installment_amount } : null
    });
  }, [updateSaleMonitor, selectedCategory, categories, selectedProductPlan, amount, selectedPlan]);

  // Función para cargar planes de un producto dinámicamente
  const fetchProductPlans = async (productId) => {
    setLoadingPlans(true);
    try {
      if (!clientData?.id) {
        console.error('No se pudo obtener el ID del cliente');
        return [];
      }

      const response = await api.get(`/customers/${clientData.id}/catalog/products/${productId}/plans`);
      if (response.data.success && response.data.data) {
        // Actualizar el estado de categories con los planes del producto
        setCategories(prevCategories => 
          prevCategories.map(cat => 
            cat.id === productId 
              ? { ...cat, plans: response.data.data }
              : cat
          )
        );
        return response.data.data;
      }
      return [];
    } catch (error) {
      if (error.response?.status === 422) {
        // Mostrar mensaje específico al usuario cuando no hay evaluación crediticia
        MySwal.fire({
          icon: 'warning',
          title: 'Evaluación crediticia requerida',
          text: error.response?.data?.message || 'No hay evaluación crediticia disponible para este cliente.',
          confirmButtonText: 'Entendido'
        });
      } else {
        console.error('Error al cargar planes del producto:', error);
      }
      return [];
    } finally {
      setLoadingPlans(false);
    }
  };

  // Función para determinar el industryKey de una categoría (versión con industry)
  const getIndustryKeyForCategory = (category) => {
    if (!category) return null;

    if (category.code || category.alias) {
      return category.code || category.alias;
    }
    if (typeof category.id === 'string') {
      return category.id;
    }
    if (category.label) {
      return category.label.toLowerCase()
        .replace(/\s+/g, '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    return category.id;
  };

  // Función para verificar si una categoría está deshabilitada (versión con industry)
  // Solo por capital agotado / productLimits. plan_source ya no deshabilita.
  const isCategoryDisabled = (category) => {
    if (!productLimits || !category) {
      return false;
    }
    
    const industryKey = getIndustryKeyForCategory(category);
    if (!industryKey) {
      return false;
    }
    
    let foundIndustryKey = null;
    
    if (productLimits[industryKey]) {
      foundIndustryKey = industryKey;
    } else {
      const availableKeys = Object.keys(productLimits);
      const normalizedInput = industryKey.toLowerCase().trim();
      
      foundIndustryKey = availableKeys.find(key => 
        key.toLowerCase() === normalizedInput
      );
      
      if (!foundIndustryKey) {
        foundIndustryKey = availableKeys.find(key => 
          key.toLowerCase().includes(normalizedInput) || 
          normalizedInput.includes(key.toLowerCase())
        );
      }
      
      if (!foundIndustryKey) {
        const normalizedInputClean = normalizedInput.replace(/[\s_-]/g, '');
        foundIndustryKey = availableKeys.find(key => {
          const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, '');
          return normalizedKey === normalizedInputClean || 
                 normalizedKey.includes(normalizedInputClean) ||
                 normalizedInputClean.includes(normalizedKey);
        });
      }
    }
    
    if (!foundIndustryKey || !productLimits[foundIndustryKey]) {
      return false;
    }
    
    const industryLimits = productLimits[foundIndustryKey];
    return industryLimits.disabled === true;
  };

  // Función para manejar selección de categoría/industry
  const handleCategorySelect = async (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedProductPlan(null);
    
    const selectedCategoryData = categories.find(cat => cat.id === categoryId);
    
    // Si el producto tiene planes, cargarlos dinámicamente
    if (selectedCategoryData && selectedCategoryData.has_plans === true) {
      const loadedPlans = await fetchProductPlans(categoryId);
      
      // Si hay solo un plan, auto-seleccionarlo
      if (loadedPlans && loadedPlans.length === 1) {
        setSelectedProductPlan(loadedPlans[0]);
      }
    }
    
    // Actualizar maxAmount desde productLimits cuando corresponde (hogartec)
    if (selectedCategoryData && productLimits) {
      const industryKey = getIndustryKeyForCategory(selectedCategoryData);
      let foundKey = productLimits[industryKey] ? industryKey : null;
      if (!foundKey) {
        const availableKeys = Object.keys(productLimits);
        const normalized = (industryKey || '').toLowerCase().trim();
        foundKey = availableKeys.find(k => k.toLowerCase() === normalized)
          || availableKeys.find(k => k.toLowerCase().includes(normalized) || normalized.includes(k.toLowerCase()))
          || availableKeys.find(k => k.toLowerCase().replace(/[\s_-]/g, '') === normalized.replace(/[\s_-]/g, ''));
      }
      if (foundKey && productLimits[foundKey]?.available_limit !== undefined) {
        setMaxAmount(productLimits[foundKey].available_limit || 0);
      }
    }
  };

  const handleRequestAuthorization = async (payload) => {
    try {
      // Crear FormData para enviar archivo adjunto
      const formData = new FormData();
      formData.append('commerce_id', channelId);
      formData.append('channel_branch_id', branchId);
      formData.append('amount', payload.amount);
      formData.append('observation', payload.observation);
      
      if (payload.password) {
        formData.append('password', payload.password);
      }
      
      if (payload.attachment) {
        formData.append('attachment', payload.attachment);
      }

      const response = await api.post(`/customers/${clientData.id}/authorizations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status === 201) {
        setShowAuthorizationModal(false);
        
        MySwal.fire({
          title: 'Operación exitosa',
          text: response.data.message,
          icon: response.data.authorization.status === 'approved' ? 'success' : 'info',
        });

        if(response.data.authorization.status === 'approved') {
        }
        
        // Return response data so modal can track authorization status
        return response.data;
      }



      MySwal.fire({
        title: 'Autorización pendiente',
        text: 'Tu solicitud ha sido enviada. Por favor espera a que sea procesada.',
        icon: 'info',
      });
      
      // Return response data so modal can track authorization status
      return response.data;
    } catch (error) {
      console.error('Error al solicitar autorización:', error);
      const errorConfig = handleValidationErrors(error);
      MySwal.fire(errorConfig);
      throw error; // Re-lanzar el error para que el modal pueda resetear el estado de loading
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    
    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      setPlans([]);
      setSelectedPlan(null);
      setLastCalculatedAmount('');
      return;
    }

    // Convertir al formato español para mostrar
    const numericValue = parseLocaleNumber(value);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toString());
      setDisplayAmount(formatNumber(numericValue));
      setPlans([]);
      setSelectedPlan(null);
    }
  };

  const handleRequestMoreCredit = (e) => {
    e?.preventDefault();
    setRequestedAmount(null);
    setShowAuthorizationModal(true);
  };

  const handleAmountValidation = () => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount <= 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Por favor, ingresa un monto válido.',
      });
      return false;
    }

    const selectedCategoryData = getSelectedCategoryData();
    const selectedPlanMaxAmount = Number(selectedProductPlan?.max_capital_effective ?? 0);
    const selectedProductMaxAmount = Number(selectedCategoryData?.max_capital_effective ?? 0);

    if (selectedPlanMaxAmount > 0 && numericAmount > selectedPlanMaxAmount) {
      MySwal.fire({
        icon: 'error',
        title: 'Límite excedido',
        text: `El monto no puede superar el capital máximo de $${formatNumber(selectedPlanMaxAmount)} para este plan.`,
        confirmButtonText: 'Entendido'
      });
      return false;
    }

    if (selectedProductMaxAmount > 0 && numericAmount > selectedProductMaxAmount) {
      MySwal.fire({
        icon: 'error',
        title: 'Límite excedido',
        text: `El monto no puede superar el capital máximo de $${formatNumber(selectedProductMaxAmount)} para este producto.`,
        confirmButtonText: 'Entendido'
      });
      return false;
    }

    if (numericAmount > maxAmountState) {
      if (authorizationsEnabled) {
        MySwal.fire({
          icon: 'warning',
          title: 'Monto excede el límite disponible',
          html: `
            El monto ingresado supera tu límite disponible por $${formatNumber(numericAmount - maxAmountState)}.<br><br>
            ¿Deseas solicitar una autorización por $${formatNumber(numericAmount)}?
          `,
          showConfirmButton: true,
          confirmButtonText: `Solicitar autorización por $${formatNumber(numericAmount)}`,
          showCancelButton: true,
          cancelButtonText: 'Cancelar'
        }).then((result) => {
          if (result.isConfirmed) {
            setRequestedAmount(numericAmount);
            setShowAuthorizationModal(true);
          }
        });
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Límite excedido',
          text: `El monto no puede superar el límite disponible de $${formatNumber(maxAmountState)}.`,
          confirmButtonText: 'Entendido'
        });
      }
      return false;
    }

    return true;
  };

  const fetchPlans = async () => {
    if (!handleAmountValidation()) return;

    // Validar que el monto haya cambiado
    if (amount === lastCalculatedAmount) {
      MySwal.fire({
        icon: 'info',
        title: 'Monto sin cambios',
        text: 'Modifica el monto para calcular nuevas cuotas.',
      });
      return;
    }

    if (!selectedCategory && categories.length > 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Categoría requerida',
        text: 'Por favor, selecciona una categoría de producto.',
      });
      return;
    }

    if (productNameConfig === 'required' && !productName.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Nombre del producto requerido',
        text: 'Por favor, ingresa el nombre del producto.',
      });
      return;
    }

    // Validación dinámica de la descripción del producto
    if (productDescriptionConfig === 'required' && !productDescription.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Descripción del producto requerida',
        text: 'Por favor, ingresa la descripción del producto.',
      });
      return;
    }

    setLoading(true);
    try {
      // Buscar la categoría seleccionada en el array de categorías para obtener su información
      const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
      
      const requestPayload = {
        amount: parseFloat(amount),
      };

      if (selectedCategoryData && selectedCategoryData.id) {
        if (selectedCategoryData.source === 'industry') {
          requestPayload.industry_category_id = selectedCategoryData.id;
        } else {
          requestPayload.policy_product_id = selectedCategoryData.id;
        }
      }

      if (selectedProductPlan && typeof selectedProductPlan.id === 'number') {
        requestPayload.policy_plan_id = selectedProductPlan.id;
      }

      // Agregar campos de producto según la configuración
      if (productNameConfig !== 'hidden') {
        requestPayload.product_name = productName.trim() || null;
      }
      
      if (productDescriptionConfig !== 'hidden') {
        requestPayload.product_description = productDescription.trim() || null;
      }

      const response = await api.post(`/customers/${clientData.id}/purchase-requests`, requestPayload);

      // Track offers searched
      if (commerceId) {
        trackOffersSearched({
          commerceId,
          branchId: analyticsBranchId,
          amount: parseFloat(amount),
          productName: productName.trim() || undefined,
          productCategory: selectedCategoryData?.name || undefined,
          customerDni: clientData?.dni
        });
      }

      setPurchaseRequestId(response.data.id);
      const updatedOffers = response.data.offers.map((offer) => ({
        ...offer,
        purchase_request_id: response.data.id,
      }));

      // Separar planes regulares y planes con anticipo
      const regularPlans = updatedOffers.filter(plan => !plan.is_downpayment_plan);
      const downpaymentPlans = updatedOffers.filter(plan => plan.is_downpayment_plan);

      // Ordenar cada grupo por cantidad de cuotas
      const sortedRegularPlans = regularPlans.sort((a, b) => a.installments - b.installments);
      const sortedDownpaymentPlans = downpaymentPlans.sort((a, b) => a.installments - b.installments);

      // Combinar los grupos ordenados
      const sortedOffers = [...sortedRegularPlans, ...sortedDownpaymentPlans];

      setLastCalculatedAmount(amount); // Guardar el monto calculado
      setPlans(sortedOffers);
      setSelectedPlan(null); // Asegurar que no hay plan seleccionado

      if (sortedOffers.length === 0) {
        MySwal.fire({
          icon: 'info',
          title: 'No hay planes disponibles',
          html: `
            <div class="text-start">
              <p class="mb-3">
                En este momento no podemos ofrecerte planes de financiación para este monto.
                Esto puede deberse a diferentes motivos:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto solicitado supera tu límite máximo de cuota mensual disponible
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto es inferior al mínimo requerido para financiar en cuotas
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  Existen restricciones temporales en tu perfil crediticio
                </li>
              </ul>
              <p class="mt-3">
                Te sugerimos:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Probar con un monto diferente
                </li>
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Consultar por otras opciones de pago
                </li>
                ${authorizationsEnabled ? `
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  <a href="#" onclick="return false;" class="text-decoration-none" id="requestMoreCredit">
                    Solicitar una autorización especial
                  </a>
                </li>
                ` : ''}
              </ul>
            </div>
          `,
          confirmButtonText: 'Entendido',
          showCloseButton: true,
          didOpen: () => {
            if (authorizationsEnabled) {
              document.getElementById('requestMoreCredit').addEventListener('click', () => {
                MySwal.close();
                setShowAuthorizationModal(true);
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error al crear la PurchaseRequest:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron obtener los planes. Por favor, intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleNext = async () => {
    if (!selectedPlan) {
      Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Por favor, selecciona un plan antes de continuar.',
      });
      return;
    }

    // Validar que el monto actual coincida con el monto calculado
    if (amount !== lastCalculatedAmount) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto modificado',
        text: 'El monto ha cambiado. Por favor, calcula las cuotas nuevamente.',
      });
      return;
    }

    try {
      const applicantId = clientData?.id;

      await api.post(
        `/customers/${applicantId}/purchase-requests/${selectedPlan.purchase_request_id}/select-offer`,
        { offer_id: selectedPlan.id }
      );

      // Guardar la oferta seleccionada en el contexto
      setSelectedOffer({
        amount: selectedPlan.financed_amount,
        originalAmount: parseFloat(amount),
        term: selectedPlan.installments,
        interestRate: selectedPlan.interest_rate,
        monthlyPayment: selectedPlan.installment_amount,
        firstDueDate: selectedPlan.first_due_date_formatted,
        displayDate: selectedPlan.display_date_formatted,
        isDownpaymentPlan: selectedPlan.is_downpayment_plan,
        downpaymentAmount: selectedPlan.downpayment_amount,
        downpaymentType: selectedPlan.downpayment_type,
        downpaymentValue: selectedPlan.downpayment_value,
      });

      // Track installment plan selected
      if (commerceId) {
        trackInstallmentPlanSelected({
          commerceId,
          branchId: analyticsBranchId,
          amount: selectedPlan.financed_amount,
          installments: selectedPlan.installments,
          interestRate: selectedPlan.interest_rate,
          customerDni: clientData?.dni,
          isDownpaymentPlan: selectedPlan.is_downpayment_plan || false,
          downpaymentType: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_type : undefined,
          downpaymentValue: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_value : undefined,
          downpaymentAmount: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_amount : undefined,
          originalAmount: selectedPlan.is_downpayment_plan ? parseFloat(amount) : undefined
        });
      }

      onNext({ selectedPlan });
    } catch (error) {
      console.error('Error al seleccionar la oferta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al seleccionar la oferta. Intenta nuevamente.',
      });
    }
  };

  // Función para determinar el paso actual del flujo
  const getCurrentStep = () => {
    if (!selectedCategory) {
      return categories.length > 0 ? 'category' : 'productForm';
    }
    
    const selectedCat = categories.find(cat => cat.id === selectedCategory);
    
    // plan_source='none': ir directo al formulario (sin paso de plan)
    if (selectedCat?.plan_source === 'none') {
      if (plans.length > 0) return 'offers';
      return 'productForm';
    }
    
    // plan_source='db' o 'code': mostrar selector de plan si hay planes y ninguno seleccionado
    const hasPlans = selectedCat?.plans && Array.isArray(selectedCat.plans) && selectedCat.plans.length > 0;
    if (hasPlans && !selectedProductPlan) {
      return 'productPlan';
    }
    
    if (plans.length > 0) {
      return 'offers';
    }
    
    return 'productForm';
  };

  const handleChangeProduct = () => {
    setSelectedCategory('');
    setAmount('');
    setDisplayAmount('');
    setSelectedPlan(null);
    setSelectedProductPlan(null);
  };

  const handleGoBack = () => {
    const currentStep = getCurrentStep();
    
    // Si hay ofertas calculadas, volver al formulario
    if (currentStep === 'offers') {
      setPlans([]);
      setSelectedPlan(null);
      setLastCalculatedAmount('');
      return;
    }
    
    // Si estamos en el formulario de producto y hay plan seleccionado, volver a selector de planes
    if (currentStep === 'productForm' && selectedProductPlan) {
      const selectedCat = categories.find(cat => cat.id === selectedCategory);
      const hasPlans = selectedCat?.plans && Array.isArray(selectedCat.plans);
      
      // Si hay solo un plan, volver directamente a selector de productos
      // porque el plan único se auto-selecciona y no hay paso intermedio
      if (hasPlans && selectedCat.plans.length === 1) {
        setSelectedCategory('');
        setSelectedProductPlan(null);
        setAmount('');
        setDisplayAmount('');
        setPlans([]);
        setSelectedPlan(null);
        return;
      }
      
      // Si hay múltiples planes, solo deseleccionar el plan para volver al selector
      setSelectedProductPlan(null);
      // Limpiar también el monto si ya se calculó
      if (plans.length > 0) {
        setPlans([]);
        setSelectedPlan(null);
        setLastCalculatedAmount('');
      }
      return;
    }
    
    // Si estamos en selector de planes, volver a selector de categorías
    if (currentStep === 'productPlan') {
      setSelectedCategory('');
      setSelectedProductPlan(null);
      return;
    }
    
    // Si estamos en el formulario sin plan seleccionado, volver a selector de categorías
    if (currentStep === 'productForm' && selectedCategory) {
      setSelectedCategory('');
      setSelectedProductPlan(null);
      setAmount('');
      setDisplayAmount('');
      setPlans([]);
      setSelectedPlan(null);
      return;
    }
  };

  const selectedCategoryData = getSelectedCategoryData();
  const effectiveMaxAmount = getEffectiveMaxAmount(selectedCategoryData);
  const productNameForTitle = selectedCategoryData?.name ?? selectedCategoryData?.label ?? '';

  return (
    <div className="row d-flex justify-content-center rg-sale-offer-step">
      <OfferStepTitle 
        currentStep={getCurrentStep()} 
        hasProductPlanSupport={hasProductPlanSupport}
        productName={productNameForTitle}
      />

      {/* Botón "Cambiar producto" - cuando hay product_limits (hogartec) */}
      {hasProductLimits && selectedCategory && (
        <ChangeProductButton
          onChangeProduct={handleChangeProduct}
        />
      )}

      {/* Selector de Categorías */}
      {(categoriesLoading || (!categoriesLoading && categories.length > 0 && !selectedCategory)) && (
        <CategorySelector
          categories={categories}
          selectedCategory={selectedCategory}
          categoriesLoading={categoriesLoading}
          onSelectCategory={handleCategorySelect}
          isCategoryDisabled={hasProductLimits ? isCategoryDisabled : undefined}
          productLimits={hasProductLimits ? productLimits : undefined}
        />
      )}

      {/* Selector de Planes de Producto - Solo si hay planes disponibles */}
      {selectedCategory && !selectedProductPlan && (() => {
        const selectedCat = categories.find(cat => cat.id === selectedCategory);
        const hasPlans = selectedCat?.plans && Array.isArray(selectedCat.plans) && selectedCat.plans.length > 0;
        // Solo mostrar si hay múltiples planes (si hay uno solo, ya debería estar auto-seleccionado)
        return loadingPlans || (hasPlans && selectedCat.plans.length > 1);
      })() && (
        <>
          <ProductPlanSelector
            categories={categories}
            selectedCategory={selectedCategory}
            selectedProductPlan={selectedProductPlan}
            onSelectPlan={setSelectedProductPlan}
            loading={loadingPlans}
            onChangeCategory={() => {
              setSelectedCategory('');
              setSelectedProductPlan(null);
            }}
          />
          {categories.length > 1 && (
            <BackButton 
              onClick={handleGoBack} 
              label="Volver a productos" 
            />
          )}
        </>
      )}

      {/* Formulario de Producto y Monto */}
      {!categoriesLoading && !loadingPlans && (selectedCategory || categories.length === 0) && (() => {
        const selectedCat = categories.find(cat => cat.id === selectedCategory);
        const hasPlans = selectedCat?.plans && Array.isArray(selectedCat.plans) && selectedCat.plans.length > 0;
        return selectedProductPlan || !hasPlans;
      })() && (
        <>
          <ProductForm
            productName={productName}
            productDescription={productDescription}
            productNameConfig={productNameConfig}
            productDescriptionConfig={productDescriptionConfig}
            onProductNameChange={(value) => setProductName(value)}
            onProductDescriptionChange={(value) => setProductDescription(value)}
            amount={amount}
            displayAmount={displayAmount}
            onAmountChange={(e) => handleAmountChange(e)}
            onCalculatePlans={fetchPlans}
            maxAmountState={effectiveMaxAmount}
            clientAuthorization={clientAuthorization}
            authorizationsEnabled={authorizationsEnabled}
            onRequestMoreCredit={handleRequestMoreCredit}
            lastCalculatedAmount={lastCalculatedAmount}
          />
          {selectedCategory && plans.length === 0 && (() => {
            const selectedCat = categories.find(cat => cat.id === selectedCategory);
            const availablePlans = selectedCat?.plans || [];
            const showBackToPlans = selectedProductPlan && availablePlans.length > 1;
            
            // Si debe mostrar "Volver a planes"
            if (showBackToPlans) {
              return (
                <BackButton 
                  onClick={handleGoBack} 
                  label="Volver a planes" 
                />
              );
            }
            
            // Si debe mostrar "Volver a productos" (solo si hay más de 1 producto)
            if (categories.length > 1) {
              return (
                <BackButton 
                  onClick={handleGoBack} 
                  label="Volver a productos" 
                />
              );
            }
            
            return null;
          })()}
        </>
      )}

      <OffersList
        plans={plans}
        selectedPlan={selectedPlan}
        onSelectPlan={selectPlan}
        onNext={handleNext}
        loading={loading}
        onGoBack={plans.length > 0 ? handleGoBack : null}
        backLabel={plans.length > 0 ? "Modificar monto" : null}
      />

      <AuthorizationModal
        show={showAuthorizationModal}
        onClose={() => setShowAuthorizationModal(false)}
        onRequest={handleRequestAuthorization}
        initialAmount={requestedAmount}
        customerDni={clientData?.dni}
        reason={requestedAmount ? 'amount_exceeds_limit' : 'manual_request'}
      />
    </div>
  );
};

export default OfferStep;
